/** The push connection behind useCanvasState: one socket per mount, reconnect, backpressure. */

import type {
  BackpressureNotice,
  CueFrame,
  QueuedNotice,
  SnapshotFrame,
  SnapshotPointer,
} from "@nebulize/sdk";

import { ApiError, request } from "./api";
import {
  applyFrame,
  cursorState,
  emptyCursor,
  parseFrame,
  resyncFrame,
  type Cursor,
  type FrameStamp,
  type StateFrame,
} from "./canvasFrames";
import { wsBaseUrl } from "./env";
import type { CanvasState } from "./runtime";
import { heldCredential } from "./tabSession";

export interface CanvasSocketHandlers {
  onOpen: () => void;
  onMessage: (data: string) => void;
  onClose: (code?: number) => void;
}

export interface CanvasSocket {
  send: (data: string) => void;
  close: () => void;
}

/** Opens one socket. Swapped out in tests, so the reconnect loop never touches a real network. */
export type SocketFactory = (
  url: string,
  protocols: string[] | undefined,
  handlers: CanvasSocketHandlers,
) => CanvasSocket;

const openWebSocket: SocketFactory = (url, protocols, handlers) => {
  const socket = new WebSocket(url, protocols);
  socket.onopen = () => handlers.onOpen();
  socket.onmessage = (event: MessageEvent<unknown>) => {
    if (typeof event.data === "string") handlers.onMessage(event.data);
  };
  socket.onclose = (event) => handlers.onClose(event.code);
  return {
    send: (data) => socket.send(data),
    close: () => socket.close(),
  };
};

/** RFC 6455's "try again later", which the api sends for a close at its connection ceiling. */
const CLOSE_CODE_TRY_AGAIN_LATER = 1013;

const BASE_DELAY_MS = 500;
const MAX_DELAY_MS = 15_000;

/** Half fixed, half random, so a whole fleet reconnecting at once does not land on one instant. */
export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** attempt);
  return Math.round(ceiling / 2 + random() * (ceiling / 2));
}

export interface CanvasStreamHandlers {
  /** The frame stamp travels with the state: it is the origin a press counts from. RUN-039. */
  onState: (state: CanvasState, frame: FrameStamp | undefined) => void;
  /** One cosmetic cue, never state. Handed over whole, so the renderer holds the dedupe key. */
  onCue: (frame: CueFrame) => void;
  onError: (message: string | null) => void;
  /** Place in the admission queue, or null on the welcome, which is when the wait is over. */
  onWaiting: (position: number | null) => void;
}

export interface CanvasStreamOptions {
  openSocket?: SocketFactory;
  fetchSnapshot?: (path: string) => Promise<SnapshotFrame>;
  schedule?: (run: () => void, delayMs: number) => () => void;
  /** Ask to look through one declared canvas. Granted to the space's owner, ignored for anyone
   * else, so what arrives is always what the welcome names. */
  asCanvas?: string;
}

export interface CanvasStreamConnection {
  close: () => void;
}

function defaultSchedule(run: () => void, delayMs: number): () => void {
  const timer = setTimeout(run, delayMs);
  return () => {
    clearTimeout(timer);
  };
}

/** One mount's push connection: dials, applies the cursor rule, reconnects on loss. */
export function openCanvasStream(
  mountId: string,
  handlers: CanvasStreamHandlers,
  options: CanvasStreamOptions = {},
): CanvasStreamConnection {
  const openSocket = options.openSocket ?? openWebSocket;
  const fetchSnapshot = options.fetchSnapshot ?? ((path: string) => request<SnapshotFrame>(path));
  const schedule = options.schedule ?? defaultSchedule;

  let closed = false;
  let attempt = 0;
  let cursor: Cursor = emptyCursor(mountId);
  let socket: CanvasSocket | undefined;
  let cancelReconnect: (() => void) | undefined;
  let backpressureWaitSeconds: number | undefined;

  function publish(next: Cursor): void {
    const changed = next !== cursor;
    cursor = next;
    if (changed && cursor.sequence !== undefined) handlers.onState(cursorState(cursor), cursor.frame);
  }

  function sendResync(reason: string): void {
    socket?.send(JSON.stringify(resyncFrame(reason, cursor)));
  }

  function handleStateFrame(frame: StateFrame): void {
    const outcome = applyFrame(cursor, frame, performance.now());
    if (outcome.kind === "resync") {
      sendResync(outcome.reason);
      return;
    }
    publish(outcome.cursor);
    handlers.onError(null);
  }

  function handlePointer(pointer: SnapshotPointer): void {
    // The key is the grant's digest, so a fetch that drops what the socket asked for computes a
    // different one and misses. Carry it, or the pointer path answers 404 forever.
    const url =
      options.asCanvas === undefined
        ? pointer.url
        : `${pointer.url}${pointer.url.includes("?") ? "&" : "?"}as_canvas=${encodeURIComponent(options.asCanvas)}`;
    fetchSnapshot(url)
      .then((body) => handleStateFrame(body))
      .catch((raised: unknown) => {
        handlers.onError(raised instanceof ApiError ? `${raised.code}: ${raised.message}` : String(raised));
        sendResync("snapshot pointer fetch failed");
      });
  }

  function handleQueued(notice: QueuedNotice): void {
    handlers.onWaiting(notice.position);
  }

  function handleBackpressure(notice: BackpressureNotice): void {
    // The reason is free text the instance writes for its own log, not a code a client can
    // match on: the close code that follows is what actually distinguishes "at capacity" from
    // any other reason a connection is being shed.
    backpressureWaitSeconds = notice.retry_after_seconds ?? undefined;
    handlers.onError(notice.reason);
  }

  function onMessage(data: string): void {
    const frame = parseFrame(data);
    if (frame === undefined) {
      sendResync("unreadable frame");
      return;
    }
    switch (frame.frame) {
      case "welcome":
        handlers.onWaiting(null);
        handleStateFrame(frame);
        return;
      case "snapshot":
      case "delta":
        handleStateFrame(frame);
        return;
      case "cue":
        // Routed off before the cursor rule: a cue carries no sequence and advances nothing.
        handlers.onCue(frame);
        return;
      case "snapshot_pointer":
        handlePointer(frame);
        return;
      case "backpressure":
        handleBackpressure(frame);
        return;
      case "queued":
        handleQueued(frame);
        return;
    }
  }

  function scheduleReconnect(): void {
    if (closed) return;
    const wait =
      backpressureWaitSeconds === undefined
        ? backoffDelayMs(attempt)
        : Math.max(backoffDelayMs(attempt), backpressureWaitSeconds * 1000);
    backpressureWaitSeconds = undefined;
    attempt += 1;
    cancelReconnect = schedule(dial, wait);
  }

  function dial(): void {
    if (closed) return;
    cursor = emptyCursor(mountId);
    const credential = heldCredential();
    const protocols = credential === undefined ? undefined : [`nebulize.tab.${credential}`];
    const asking =
      options.asCanvas === undefined
        ? ""
        : `?as_canvas=${encodeURIComponent(options.asCanvas)}`;
    socket = openSocket(`${wsBaseUrl}/v1/mounts/${mountId}/stream${asking}`, protocols, {
      onOpen: () => {
        attempt = 0;
      },
      onMessage,
      onClose: (code) => {
        socket = undefined;
        if (code === CLOSE_CODE_TRY_AGAIN_LATER) handlers.onError("the lobby is full, retrying");
        scheduleReconnect();
      },
    });
  }

  dial();

  return {
    close: () => {
      closed = true;
      cancelReconnect?.();
      socket?.close();
    },
  };
}
